/**
 * Server-side helper functions
 * These functions can be used in both server and client components
 */

import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import {
  SIDEBAR_PAGE_SIZE_DEFAULT,
  SIDEBAR_PAGE_SIZE_TAMDA_MEDIA,
  SIDEBAR_PAGE_SIZE_TAMDA_ONE,
} from "@/constants/constant"
import { SiteId } from "@/sites/config"

/**
 * Helper function to build category path
 * @param url - URL string to process
 * @returns Formatted path
 */
export const linkCategoryPath = (url: string) => {
  if (!url) return "/"

  // Handle special cases like "#" or external URLs
  if (url === "#" || url.startsWith("http://") || url.startsWith("https://") || url.startsWith("mailto:")) {
    return url
  }

  // Remove leading slash if exists
  const cleanUrl = url.startsWith("/") ? url.slice(1) : url
  // If URL is homepage-vi (in any format), return root path
  if (
    cleanUrl === "homepage-vi" ||
    cleanUrl.endsWith("/homepage-vi") ||
    cleanUrl === "vi/homepage-vi" ||
    cleanUrl.includes("homepage-vi")
  ) {
    return "/"
  }

  return `/${cleanUrl}`
}

export const linkPagePath = (url: string) => {
  if (!url) return "/"

  // Handle special cases like "#" or external URLs
  if (url === "#" || url.startsWith("http://") || url.startsWith("https://") || url.startsWith("mailto:")) {
    return url
  }

  // Remove leading slash if exists
  const cleanUrl = url.startsWith("/") ? url.slice(1) : url
  if (
    cleanUrl === "homepage-vi" ||
    cleanUrl.endsWith("/homepage-vi") ||
    cleanUrl === "vi/homepage-vi" ||
    cleanUrl.includes("homepage-vi")
  ) {
    return ""
  }

  return `/${url}`
}

/**
 * Helper function to build image URL
 * If URL already starts with http:// or https://, return as is
 * Otherwise, prepend ADMIN_API_CONFIG.API_URL to the URL
 * @param url - Image URL string to process
 * @returns Full image URL
 */
export const buildImageUrl = (url: string | null | undefined): string => {
  if (!url) return ""

  // If URL already starts with http:// or https://, return as is
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url
  }

  // Otherwise, prepend ADMIN_API_CONFIG.API_URL
  const baseUrl = ADMIN_API_CONFIG.API_URL || ""
  const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl
  const cleanUrl = url.startsWith("/") ? url : `/${url}`

  return `${cleanBaseUrl}${cleanUrl}`
}

export function getDomainFromHost(host?: string | null): string {
  if (!host) return "tamdamedia.eu"

  const hostname = host.split(":")[0].toLowerCase()

  const domain = hostname.startsWith("www.") ? hostname.slice(4) : hostname

  return domain
}

export function getDomainFromStoreCookie(cookieValue?: string | null): string | null {
  if (!cookieValue) return null

  try {
    const parsed = JSON.parse(cookieValue)
    const selectedDomain = parsed?.state?.selectedDomain

    if (selectedDomain?.attributes?.domain) {
      return selectedDomain.attributes.domain
    }

    return null
  } catch (error) {
    console.error("Error parsing domain store cookie:", error)
    return null
  }
}

export function getSidebarPostsPageSize(siteId: SiteId, host?: string | null): string {
  switch (siteId) {
    case "tamdamedia.eu":
      return SIDEBAR_PAGE_SIZE_TAMDA_MEDIA
    case "tamdagroup.eu":
      return SIDEBAR_PAGE_SIZE_TAMDA_ONE
    default:
      return SIDEBAR_PAGE_SIZE_DEFAULT
  }
}
